import { DateFormat, coalescePlainTextToEditorV2Html } from '@equip.health/ui';
import { some } from 'lodash';
import { DateTime } from 'luxon';

import {
  type ContactInfoValue,
  type CoverageInfoValue,
  type InquiryInfoValue,
  type InquiryValues,
  type NoteInfoValue,
  type PatientInfoValue,
  type ReferralInfoValue,
  CLASS_MISSING_REQUIRED,
} from '~/components/common/inquiry/drawer/types';
import { NoteStatus } from '~/lib/constants/AddPatientNoteModal.constants';
import { OTHER_PAYOR } from '~/lib/constants/inquiry.constants';
import { ActivityWith } from '~/lib/util/classes/groupClasses.util';
import {
  getContactsWithRequiredFields,
  isAnyFieldFilled,
  validateInquiryRequiredFields,
} from '~/lib/util/inquiry.util';
import { convertPhoneNumberToInternationalFormat } from '~/lib/util/patient.util';
import util, { emptyToNull } from '~/lib/util/util';
import type { PatientCoverage } from '~/lib/constants/patients.constants';

const formatDate = (date: string): string | undefined =>
  date
    ? DateTime.fromJSDate(new Date(date)).toUTC().toFormat('LL/dd/yyyy')
    : undefined;

/**
 * Check if any mandatory inquiry fields are missing.
 * @param inquiry InquiryInfoValue instance
 */
export const isInquiryMissingFields = (inquiry: InquiryInfoValue): boolean => {
  const {
    inquirerRole,
    equipSourceId,
    informationSourceId,
    isClosed,
    closedReasonId,
  } = inquiry;

  return (
    // Required fields for InquiryInfo
    !inquirerRole ||
    !equipSourceId ||
    !informationSourceId ||
    // Status is closed and there's no closed reason
    (isClosed && !closedReasonId)
  );
};

/**
 * Check if any mandatory patient fields are missing.
 * @param patient PatientInfoValue instance
 */
export const isPatientMissingFields = (patient: PatientInfoValue): boolean => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    state,
    languages,
    isPrimaryContact,
    isInvalidEmail,
    isInvalidPhone,
  } = patient;

  const isEmailEmpty = util.isEmptyString(email);
  const isPhoneEmpty = util.isEmptyString(phoneNumber);
  const isEmailInvalidPrimary = isEmailEmpty || isInvalidEmail;
  const isPhoneInvalidPrimary = isPhoneEmpty || isInvalidPhone;
  const isEmailInvalidNonPrimary = !isEmailEmpty && isInvalidEmail;
  const isPhoneInvalidNonPrimary = !isPhoneEmpty && isInvalidPhone;

  return (
    isEmailInvalidNonPrimary ||
    isPhoneInvalidNonPrimary ||
    util.isEmptyString(firstName) ||
    util.isEmptyString(lastName) ||
    util.isEmptyString(state) ||
    !languages?.[0] ||
    (isPrimaryContact && isPhoneInvalidPrimary && isEmailInvalidPrimary)
  );
};

/**
 * Check if any mandatory contact fields are missing.
 * @param contact ContactInfoValue instance
 */
export const isContactMissingFields = (contact: ContactInfoValue): boolean => {
  const {
    firstName,
    lastName,
    cellPhone,
    email,
    relationToPatient,
    isPrimaryContact,
    isInvalidCellPhone,
    isInvalidEmail,
  } = contact;
  const areRequiredFieldsValid = validateInquiryRequiredFields({
    firstName,
    lastName,
    relationToPatient,
  });
  const isEmailEmpty = util.isEmptyString(email);
  const isPhoneEmpty = util.isEmptyString(cellPhone);
  const isEmailInvalidPrimary = isEmailEmpty || isInvalidEmail;
  const isPhoneInvalidPrimary = isPhoneEmpty || isInvalidCellPhone;
  const isEmailInvalidNonPrimary = !isEmailEmpty && isInvalidEmail;
  const isPhoneInvalidNonPrimary = !isPhoneEmpty && isInvalidCellPhone;

  return (
    isPhoneInvalidNonPrimary ||
    isEmailInvalidNonPrimary ||
    (isAnyFieldFilled(contact) &&
      (!areRequiredFieldsValid ||
        (isPrimaryContact === true &&
          isEmailInvalidPrimary &&
          isPhoneInvalidPrimary)))
  );
};

/**
 * Check if any mandatory coverage fields are missing.
 * @param coverage CoverageInfoValue instance
 */
export const isCoverageMissingFields = (
  coverage: CoverageInfoValue,
): boolean => {
  return (
    !coverage.payor ||
    (coverage.payor === OTHER_PAYOR && !coverage.customPayorName)
  );
};

/**
 * Check if any mandatory referral fields are missing.
 * @param referral ReferralInfoValue instance
 */
export const isReferralMissingFields = (
  referral: ReferralInfoValue,
): boolean => {
  const {
    firstName,
    lastName,
    email,
    officePhone,
    referrerType,
    relationToPatient,
    isPrimaryContact,
    isInvalidOfficePhone,
    isInvalidEmail,
  } = referral;
  const areRequiredFieldsValid = validateInquiryRequiredFields({
    firstName,
    lastName,
    referrerType,
    relationToPatient,
  });
  const isEmailEmpty = util.isEmptyString(email);
  const isPhoneEmpty = util.isEmptyString(officePhone);
  const isEmailInvalidPrimary = isEmailEmpty || isInvalidEmail;
  const isPhoneInvalidPrimary = isPhoneEmpty || isInvalidOfficePhone;
  const isEmailInvalidNonPrimary = !isEmailEmpty && isInvalidEmail;
  const isPhoneInvalidNonPrimary = !isPhoneEmpty && isInvalidOfficePhone;
  return (
    isPhoneInvalidNonPrimary ||
    isEmailInvalidNonPrimary ||
    (isAnyFieldFilled(referral) &&
      (!areRequiredFieldsValid ||
        (isPrimaryContact === true &&
          isPhoneInvalidPrimary &&
          isEmailInvalidPrimary)))
  );
};

/**
 * Check if any mandatory fields are missing from a create/edit inquiry operation.
 * @param inquiry Inquiry Values instance.
 */
export const isMissingFields = (inquiry: InquiryValues): boolean => {
  const { info, patient, coverages, referral, contacts } = inquiry;
  return (
    isInquiryMissingFields(info) ||
    isPatientMissingFields(patient) ||
    some(contacts, isContactMissingFields) ||
    isCoverageMissingFields(coverages) ||
    isReferralMissingFields(referral)
  );
};

/**
 * Create from API response
 * @param inquiry Api response
 */
export const getInquiryInitialValue = (
  inquiry: InquiryDetailResponse,
): InquiryInfoValue => {
  return {
    closedReasonId: inquiry?.closedReason?.id,
    equipSourceId: inquiry?.equipSource.id,
    id: inquiry?.id,
    informationSourceId: inquiry?.informationSource.id,
    inquirerRole: inquiry?.inquirerRole.name,
    inquiryDate: inquiry?.inquiryDate
      ? new Date(inquiry.inquiryDate)
      : undefined,
    isClosed: inquiry?.status
      ? inquiry.status.name.toLowerCase() === 'closed'
      : undefined,
    patientAge: inquiry?.patientAge ?? undefined,
    statusId: inquiry?.status?.id,
    targetTreatmentProgramName: inquiry?.targetTreatmentProgram?.description,
    tellUsMore: inquiry?.tellUsMore,
  };
};

/**
 * Create from API response
 * @param patient Patient Api response
 * @param primaryContactId the primary contact id
 */
export const getPatientInitialValue = (
  patient: Patient,
  primaryContactId?: string,
): PatientInfoValue => {
  // Is primary is true if no other contact is primary
  const isPrimary =
    !primaryContactId &&
    !some(patient?.contacts, (p) => p.contactExternalId === primaryContactId);
  const dob = formatDate(patient?.dateOfBirth);

  const patienthasAppAccess = patient?.hasAuthId && patient?.isAccountActive;

  return {
    chosenName: patient?.chosenName,
    dateOfBirth: dob,
    email: patient?.email,
    firstName: patient?.firstName,
    genderOnRecord: patient?.genderOnRecord,
    hasAppAccess: !!patienthasAppAccess,
    hasGivenRecordingConsent: patient?.hasGivenRecordingConsent,
    id: patient?.userId,
    isPrimaryContact: isPrimary,
    languages: patient?.languages,
    lastName: patient?.lastName,
    phoneNumber: patient?.phoneNumber,
    pronounsOnRecord: patient?.pronounsOnRecord,
    state: patient?.state,
    timezone: patient?.timezone,
  };
};

export const getCoveragesInitialValue = (
  coverages: PatientCoverage[],
): CoverageInfoValue[] => {
  return coverages
    ? coverages.map((coverage) => {
        return {
          alternativeId: coverage.alternativeId,
          coverageExternalId: coverage.coverageExternalId,
          coveragePriority: coverage.coveragePriority,
          customPayorName: coverage.customPayorName,
          endDate: coverage.endDate,
          groupId: coverage.groupId,
          isActive: coverage.isActive,
          patientRelationToSubscriber: coverage.patientRelationToSubscriber,
          payor: coverage.payor,
          providerServicesPhoneNumber: coverage.providerServicesPhoneNumber,
          startDate: coverage.startDate,
          stateCode: coverage.stateCode,
          subscriberDateOfBirth: formatDate(coverage.subscriberDateOfBirth),
          subscriberFirstName: coverage.subscriberFirstName,
          subscriberId: coverage.subscriberId,
          subscriberLastName: coverage.subscriberLastName,
        };
      })
    : [];
};

/**
 * Create from API response
 * @param patientContacts Patient Contacts Api response
 * @param nonFamilyRelationsToPatientInPatientContact relation to patient enumeration for determining referrer type.
 */
export const getContactsInitialValue = (
  patientContacts: Partial<PatientContact>[],
  nonFamilyRelationsToPatientInPatientContact: RelationToPatientEnumeration[],
): Readonly<[ReferralInfoValue, ContactInfoValue[]]> => {
  const contacts: ContactInfoValue[] = [];
  let referrer: ReferralInfoValue = {};

  if (patientContacts) {
    patientContacts.forEach((contact) => {
      if (contact.isReferringProvider) {
        referrer = {
          addressLine1: contact.address?.addressLine1,
          addressLine2: contact.address?.addressLine2,
          city: contact.address?.city,
          contactExternalId: contact.contactExternalId,
          email: contact.email,
          faxNumber: contact.faxNumber,
          firstName: contact.firstName,
          isOngoingProvider: contact.isOngoingProvider,
          isPrimaryContact: !!contact.isPrimaryContact,
          lastName: contact.lastName,
          latitude: contact.address?.latitude,
          longitude: contact.address?.longitude,
          name: contact?.address.affiliationName,
          officeExtension: contact.officeExtension
            ? contact.officeExtension.toString()
            : undefined,
          officePhone: contact.officePhone,
          prefix: contact.prefix,
          referrerType: nonFamilyRelationsToPatientInPatientContact.find(
            (relation: RelationToPatientEnumeration) =>
              relation.label === contact.relationToPatient,
          )?.subcategory,
          relationToPatient: contact.relationToPatient,
          state: contact.address?.state,
          timezone: contact.timezone,
          zipCode: contact.address?.zip,
        };
      } else {
        contacts.push({
          cellPhone: contact.cellPhone,
          chosenName: contact.chosenName,
          contactExternalId: contact.contactExternalId,
          email: contact.email,
          firstName: contact.firstName,
          isPrimaryContact: !!contact.isPrimaryContact,
          isSupport: !!contact.isSupport,
          languages: contact.languages,
          lastName: contact.lastName,
          pronouns: contact.pronouns,
          relationToPatient: contact.relationToPatient,
          timezone: contact.timezone,
        });
      }
    });
  }

  return [referrer, contacts];
};

/**
 * Create a persist note request.
 * @param inquiryId inquiry id to associate with note
 * @param userExternalId user creating the note
 * @param patientExternalId patient associated with note
 * @param note note value
 */
export const createNoteRequest = (
  inquiryId: string,
  userExternalId: string,
  patientExternalId: string,
  note: NoteInfoValue,
): PersistNoteRequest => {
  return {
    activityDate: new Date().toISOString(),
    activityMedium: 'Inbound phone',
    activityWith: [{ activityWithName: note.activityWithName }],
    authoredByProviderExternalId: userExternalId,
    inquiryId,
    noteContent: note.content,
    noteStatus: NoteStatus.SIGNED,
    noteType: note.isQualifyingCall ? 'Qualifying call' : 'Admissions',
    patientExternalId,
    title: note.isQualifyingCall ? 'Qualifying Call' : 'Admissions Touchpoint',
  };
};

interface QuickNoteRequestParams {
  activityMedium: string;
  inquiryId: string;
  isPatientPrimaryContact: boolean;
  noteContent?: string;
  patientExternalId: string;
  userExternalId: string;
}

export const createQuickNoteRequest = (
  params: QuickNoteRequestParams,
): PersistNoteRequest => {
  const {
    activityMedium,
    inquiryId,
    isPatientPrimaryContact,
    noteContent,
    patientExternalId,
    userExternalId,
  } = params;

  const activityWithName: ActivityWith = isPatientPrimaryContact
    ? ActivityWith.PATIENT
    : ActivityWith.CARER;

  return {
    activityDate: new Date().toISOString(),
    activityMedium: '', //TODO: Confirm NoteRequest params - activityMedium is not returned in Notes response
    activityWith: [{ activityWithName }],
    authoredByProviderExternalId: userExternalId,
    inquiryId,
    noteCommunicationMethod: activityMedium,
    noteContent: coalescePlainTextToEditorV2Html(noteContent ?? ''),
    noteStatus: NoteStatus.SIGNED,
    noteType: 'Admissions',
    patientExternalId,
    title: activityMedium,
  };
};

export const scrollToMissingRequired = () => {
  const firstMissingRequired = document.querySelector(
    `.${CLASS_MISSING_REQUIRED}`,
  );
  if (firstMissingRequired) {
    firstMissingRequired.scrollIntoView({ behavior: 'smooth' });
  }
};

const createReferral = (
  referral: ReferralInfoValue,
  inquiryId: string,
): InquiryPatientContact[] => {
  if (
    referral &&
    isAnyFieldFilled(referral) &&
    !isReferralMissingFields(referral)
  ) {
    const officeExtension =
      referral.officeExtension !== undefined
        ? emptyToNull(referral.officeExtension.toString())
        : undefined;

    const rawOfficePhone = emptyToNull(referral.officePhone);
    const officePhone = rawOfficePhone
      ? convertPhoneNumberToInternationalFormat(rawOfficePhone)
      : undefined;
    return [
      {
        addressLine1: referral.addressLine1,
        addressLine2: referral.addressLine2,
        affiliationName: referral.name,
        city: referral.city,
        email: referral.email,
        faxNumber: referral.faxNumber,
        firstName: referral.firstName,
        id: referral.contactExternalId,
        inquiryId,
        isOngoingProvider: referral.isOngoingProvider,
        isPrimaryContact: referral.isPrimaryContact === true,
        isReferringProvider: true,
        lastName: referral.lastName,
        latitude: referral.latitude,
        longitude: referral.longitude,
        officeExtension,
        officePhone,
        prefix: referral.prefix,
        relationToPatient: referral.relationToPatient,
        state: referral.state,
        timezone: referral.timezone,
        zipCode: referral.zipCode,
      },
    ];
  }
  return [];
};

export const createCoverage = (
  coverages: CoverageInfoValue,
  inquiryId: string,
): InquiryCoverage => {
  return {
    alternativeId: coverages.alternativeId,
    coveragePriority: coverages.coveragePriority,
    customPayorName: coverages.customPayorName,
    endDate:
      coverages.endDate &&
      DateTime.fromISO(coverages.endDate).toFormat(
        DateFormat.shortYearMonthDay,
      ),
    groupId: coverages.groupId,
    id: coverages.coverageExternalId,
    inquiryId,
    isActive: coverages.isActive,
    patientRelationToSubscriber: coverages.patientRelationToSubscriber,
    payor: coverages.payor,
    providerServicesPhoneNumber: coverages.providerServicesPhoneNumber,
    startDate:
      coverages.startDate &&
      DateTime.fromISO(coverages.startDate).toFormat(
        DateFormat.shortYearMonthDay,
      ),
    stateCode: coverages.stateCode,
    subscriberDateOfBirth:
      coverages.subscriberDateOfBirth &&
      DateTime.fromJSDate(new Date(coverages.subscriberDateOfBirth)).toFormat(
        DateFormat.shortYearMonthDay,
      ),
    subscriberFirstName: coverages.subscriberFirstName,
    subscriberId: coverages.subscriberId,
    subscriberLastName: coverages.subscriberLastName,
  };
};

const createPatient = (patient: PatientInfoValue): InquiryPatient => {
  return {
    chosenName: patient.chosenName,
    dateOfBirth:
      patient.dateOfBirth &&
      DateTime.fromFormat(patient.dateOfBirth, DateFormat.longDate).toFormat(
        DateFormat.shortYearMonthDay,
      ),
    email: patient.email,
    firstName: patient.firstName,
    genderOnRecord: patient.genderOnRecord,
    hasGivenRecordingConsent: patient.hasGivenRecordingConsent,
    id: patient.id,
    languages: patient.languages,
    lastName: patient.lastName,
    phoneNumber: convertPhoneNumberToInternationalFormat(patient.phoneNumber),
    pronounsOnRecord: patient.pronounsOnRecord,
    state: patient.state,
    timezone: patient.timezone,
  };
};

const createContact = (
  contact: ContactInfoValue,
  inquiryId: string,
): InquiryPatientContact => {
  return {
    cellPhone: convertPhoneNumberToInternationalFormat(contact.cellPhone),
    chosenName: contact.chosenName,
    email: contact.email,
    firstName: contact.firstName,
    id: contact.contactExternalId,
    inquiryId,
    isPrimaryContact: !!contact.isPrimaryContact, // Cannot be undefined
    isReferringProvider: false, // Cannot be undefined
    languages: contact.languages,
    lastName: contact.lastName,
    pronouns: contact.pronouns,
    relationToPatient: contact.relationToPatient,
    timezone: contact.timezone,
  };
};

const buildTargetTreatmentProgramObject = (
  info: InquiryInfoValue,
): InquiryEnumV2 => {
  if (!info?.targetTreatmentProgramName) return undefined;
  return {
    description: info.targetTreatmentProgramName,
  };
};

export type InquiryUpdate = {
  contacts: ContactInfoValue[];
  coverage: CoverageInfoValue;
  info: InquiryInfoValue;
  patient: PatientInfoValue;
  referral: ReferralInfoValue;
};

/**
 * Create a CreateInquiryRequest from InquiryValues UI state.
 * @param value UI state
 */
export const createInquiryRequest = (
  value: InquiryUpdate,
): CreateInquiryRequest => {
  const { coverage, referral, patient, contacts, info } = value;
  return {
    coverages: [createCoverage(coverage, info.id)],
    equipSourceId: info.equipSourceId,
    informationSourceId: info.informationSourceId,
    inquirerRole: info.inquirerRole,
    inquiryDate: info.inquiryDate && info.inquiryDate.toISOString(),
    inquiryNotes: info.inquiryNote,
    patient: createPatient(patient),
    patientAge: info.patientAge,
    patientContacts: [
      ...getContactsWithRequiredFields(contacts).map((contact) => {
        return createContact(contact, info.id);
      }),
      ...createReferral(referral, info.id),
    ],
    targetTreatmentProgram: buildTargetTreatmentProgramObject(info),
    tellUsMore: info.tellUsMore,
  };
};

export type CurrentState = {
  contacts: PatientContact[];
  coverage: PatientCoverage;
  patient: Patient;
  version: string;
};

const updateContact = (
  update: ContactInfoValue,
  inquiryId: string,
  existingContact?: PatientContact,
): InquiryPatientContact => {
  return existingContact
    ? {
        addressLine1: existingContact.address?.addressLine1,
        addressLine2: existingContact.address?.addressLine2,
        affiliationName: existingContact.address?.affiliationName,
        cellPhone: update.cellPhone,
        chosenName: update.chosenName,
        city: existingContact.address?.city,
        country: existingContact.address?.country,
        email: update.email,
        firstName: update.firstName,
        id: existingContact.contactExternalId,
        inquiryId,
        isPrimaryContact: update.isPrimaryContact,
        isReferringProvider: false,
        isOngoingProvider: existingContact.isOngoingProvider,
        languages: update.languages,
        lastName: update.lastName,
        latitude: existingContact.address?.latitude,
        longitude: existingContact.address?.longitude,
        officeExtension: existingContact.officeExtension,
        officePhone: emptyToNull(existingContact.officePhone),
        prefix: existingContact.prefix,
        pronouns: update.pronouns,
        relationToPatient: update.relationToPatient,
        state: existingContact.address?.state,
        timezone: update.timezone,
        zipCode: existingContact.address?.zip,
      }
    : createContact(update, inquiryId);
};

/**
 * Create an UpdateInquiryRequestV2 from InquiryValues UI state.
 * @param value UI state
 * @param currentState Current inquiry state
 */
export const createUpdateInquiryRequest = (
  value: InquiryUpdate,
  currentState: CurrentState,
): UpdateInquiryRequestV2 => {
  const { coverage, referral, patient, contacts, info } = value;

  return {
    closedReasonId: info.closedReasonId,
    coverages: [createCoverage(coverage, info.id)],
    equipSourceId: info.equipSourceId,
    informationSourceId: info.informationSourceId,
    inquirerRole: info.inquirerRole,
    inquiryDate: info.inquiryDate && info.inquiryDate.toISOString(),
    inquiryNotes: info.inquiryNote,
    patient: createPatient(patient),
    patientAge: info.patientAge,
    patientContacts: [
      ...getContactsWithRequiredFields(contacts).map((contact) => {
        let existingContact: PatientContact;
        if (contact.contactExternalId) {
          existingContact = currentState.contacts.find(
            (x) => x.contactExternalId === contact.contactExternalId,
          );
        }
        return updateContact(contact, info.id, existingContact);
      }),
      ...createReferral(referral, info.id),
    ],
    statusId: info.statusId,
    targetTreatmentProgram: buildTargetTreatmentProgramObject(info),
    tellUsMore: info.tellUsMore,
    version: currentState.version,
  };
};
